// src/pages/PricingPage.js
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Check,
    Star,
    Code2,
    Database,
    Bot,
    Users,
    Zap,
    Shield,
    ArrowRight,
    MessageCircle,
    X,
    AlertCircle
} from 'lucide-react';
import { AuthContext } from '../App';
import SubscriptionStatus from '../components/subscription/SubscriptionStatus';
import { subscriptionsApi } from '../services/api';

const PricingPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAuthenticated, token } = useContext(AuthContext);
    const [selectedPlan, setSelectedPlan] = useState('standard');
    const [showContactForm, setShowContactForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        company: '',
        message: '',
        plan_type: 'enterprise',
        phone: '',
        team_size: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [emailError, setEmailError] = useState('');

    // Email validation function
    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    const plans = [
        {
            id: 'free',
            name: t('pricing.plans.free.name', 'Free'),
            price: 0,
            period: t('pricing.plans.free.period', 'forever'),
            description: t('pricing.plans.free.description', 'Perfect for learning and getting started'),
            popular: false,
            features: [
                { name: t('pricing.features.sandboxes', 'Sandboxes'), value: '1', icon: Code2 },
                { name: t('pricing.features.templates', 'Templates'), value: '2', icon: Database },
                { name: t('pricing.features.components', 'Components per Sandbox'), value: '10', icon: Code2 },
                { name: t('pricing.features.tutors', 'AI Tutors'), value: '1', icon: Bot },
                { name: t('pricing.features.repositories', 'Material Repositories'), value: '1 (10MB)', icon: Database },
                { name: t('pricing.features.groups', 'Groups'), value: '10', icon: Users },
                { name: t('pricing.features.api_requests', 'API Requests'), value: '100/day', icon: Zap },
            ],
            buttonText: t('pricing.buttons.get_started_free', 'Get Started Free'),
            buttonStyle: 'bg-gray-900 text-white hover:bg-gray-800',
            cardStyle: 'border-gray-200 bg-white'
        },
        {
            id: 'standard',
            name: t('pricing.plans.standard.name', 'Standard'),
            price: 10,
            period: t('pricing.plans.standard.period', 'month'),
            description: t('pricing.plans.standard.description', 'For professionals and growing teams'),
            popular: true,
            features: [
                { name: t('pricing.features.sandboxes', 'Sandboxes'), value: '5', icon: Code2 },
                { name: t('pricing.features.templates', 'Templates'), value: '10', icon: Database },
                { name: t('pricing.features.components', 'Components per Sandbox'), value: '20', icon: Code2 },
                { name: t('pricing.features.tutors', 'AI Tutors'), value: '5', icon: Bot },
                { name: t('pricing.features.repositories', 'Material Repositories'), value: '5 (50MB each)', icon: Database },
                { name: t('pricing.features.groups', 'Groups'), value: '50', icon: Users },
                { name: t('pricing.features.api_requests', 'API Requests'), value: '1000/day', icon: Zap },
                { name: t('pricing.features.course_constructor', 'Course & Assignment Constructor with Sandboxes and AI Tutors'), value: t('pricing.included', 'Included'), icon: Code2 },
                { name: t('pricing.features.priority_support', 'Priority Support'), value: t('pricing.included', 'Included'), icon: Shield },
            ],
            buttonText: t('pricing.buttons.contact_for_standard', 'Contact for Standard'),
            buttonStyle: 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700',
            cardStyle: 'border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 relative'
        },
        {
            id: 'enterprise',
            name: t('pricing.plans.enterprise.name', 'Enterprise'),
            price: t('pricing.custom', 'Custom'),
            period: '',
            description: t('pricing.plans.enterprise.description', 'Tailored solutions for large organizations'),
            popular: false,
            features: [
                { name: t('pricing.features.unlimited_sandboxes', 'Unlimited Sandboxes'), value: '∞', icon: Code2 },
                { name: t('pricing.features.unlimited_templates', 'Unlimited Templates'), value: '∞', icon: Database },
                { name: t('pricing.features.custom_components', 'Custom Components'), value: t('pricing.unlimited', 'Unlimited'), icon: Code2 },
                { name: t('pricing.features.unlimited_tutors', 'Unlimited AI Tutors'), value: '∞', icon: Bot },
                { name: t('pricing.features.custom_storage', 'Custom Storage'), value: t('pricing.as_needed', 'As needed'), icon: Database },
                { name: t('pricing.features.advanced_groups', 'Advanced Groups'), value: t('pricing.unlimited', 'Unlimited'), icon: Users },
                { name: t('pricing.features.unlimited_api', 'Unlimited API'), value: t('pricing.no_limits', 'No limits'), icon: Zap },
                { name: t('pricing.features.course_constructor', 'Advanced Course & Assignment Constructor with Sandboxes and AI Tutors'), value: t('pricing.included', 'Included'), icon: Code2 },
                { name: t('pricing.features.dedicated_support', '24/7 Dedicated Support'), value: t('pricing.included', 'Included'), icon: Shield },
                { name: t('pricing.features.custom_integrations', 'Custom Integrations'), value: t('pricing.available', 'Available'), icon: MessageCircle },
                { name: t('pricing.features.sla_guarantee', 'SLA Guarantee'), value: '99.9%', icon: Shield },
            ],
            buttonText: t('pricing.buttons.contact_sales', 'Contact Sales'),
            buttonStyle: 'bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:from-gray-800 hover:to-gray-600',
            cardStyle: 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100'
        }
    ];

    const handleSubmitContactForm = async () => {
        if (!formData.name || !formData.email) return;

        // Validate email before submission
        if (!validateEmail(formData.email)) {
            setEmailError(t('pricing.contact_form.invalid_email', 'Please enter a valid email address'));
            return;
        }

        setIsSubmitting(true);

        try {
            const requestData = {
                name: formData.name,
                email: formData.email,
                company: formData.company,
                message: formData.message,
                plan_type: formData.plan_type,
                phone: formData.phone,
                team_size: formData.team_size ? parseInt(formData.team_size) : null
            };

            await subscriptionsApi.submitRequest(requestData, token);

            setSubmitMessage(t('pricing.contact_form.success_message', 'Thank you! We\'ll contact you within 24 hours.'));
            setFormData({
                name: '',
                email: '',
                company: '',
                message: '',
                plan_type: 'enterprise',
                phone: '',
                team_size: ''
            });
            setEmailError('');

            setTimeout(() => {
                setShowContactForm(false);
                setSubmitMessage('');
            }, 3000);
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setSubmitMessage(t('pricing.contact_form.error_message', 'Something went wrong. Please try again.'));
        }

        setIsSubmitting(false);
    };

    const handlePlanSelect = (planId) => {
        setSelectedPlan(planId);
        if (planId === 'enterprise') {
            setFormData(prev => ({ ...prev, plan_type: 'enterprise' }));
            setShowContactForm(true);
        } else if (planId === 'free') {
            // Free plan is only for non-authenticated users and redirects to registration
            navigate('/?register=true');
        } else if (planId === 'standard') {
            setFormData(prev => ({ ...prev, plan_type: 'standard' }));
            setShowContactForm(true);
        }
    };

    return (
        <div className="min-h-screen min-w-screen w-full bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            {/* Hero Section */}
            <div className="relative overflow-hidden pt-24 md:pt-32">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-blue-600/10"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="text-center">
                        <h1 className="text-5xl font-bold text-gray-900 mb-6">
                            {t('pricing.hero.title_part1', 'Choose Your')}{' '}
                            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                {t('pricing.hero.title_part2', 'Perfect Plan')}
              </span>
                        </h1>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
                            {t('pricing.hero.subtitle', 'Scale your development with our powerful platform. From learning to enterprise solutions, we have the perfect plan for your needs.')}
                        </p>

                        {/* Current Subscription Status for Authenticated Users */}
                        {isAuthenticated && (
                            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200 max-w-2xl mx-auto mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    {t('pricing.current_subscription', 'Your Current Subscription')}
                                </h3>
                                <SubscriptionStatus token={token} />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pricing Cards */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 pb-20 relative z-10 bg-white bg-opacity-90 rounded-2xl shadow-lg">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {plans.filter(plan => plan.id !== 'free' || !isAuthenticated).map((plan) => (
                        <div
                            key={plan.id}
                            className={`
                relative rounded-2xl p-8 shadow-xl border-2 transition-all duration-300
                hover:shadow-2xl hover:scale-105 cursor-pointer
                ${plan.cardStyle}
                ${selectedPlan === plan.id ? 'ring-4 ring-purple-500 ring-opacity-50' : ''}
              `}
                            onClick={() => setSelectedPlan(plan.id)}
                        >
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold flex items-center">
                                        <Star className="w-4 h-4 mr-1 fill-current" />
                                        {t('pricing.most_popular', 'Most Popular')}
                                    </div>
                                </div>
                            )}

                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                                <div className="mb-4">
                                    {typeof plan.price === 'number' ? (
                                        <div>
                                            <span className="text-5xl font-bold text-gray-900">${plan.price}</span>
                                            <span className="text-gray-600">/{plan.period}</span>
                                        </div>
                                    ) : (
                                        <div className="text-4xl font-bold text-gray-900">{plan.price}</div>
                                    )}
                                </div>
                                <p className="text-gray-600">{plan.description}</p>
                            </div>

                            <div className="space-y-4 mb-8">
                                {plan.features.map((feature, idx) => (
                                    <div key={idx} className="flex items-center">
                                        <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                            <Check className="w-3 h-3 text-green-600" />
                                        </div>
                                        <div className="flex items-center flex-1">
                                            <feature.icon className="w-4 h-4 text-gray-500 mr-2" />
                                            <span className="text-gray-700 flex-1">{feature.name}</span>
                                            <span className="text-gray-900 font-semibold">{feature.value}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePlanSelect(plan.id);
                                }}
                                className={`
                  w-full py-4 px-6 rounded-xl font-semibold transition-all duration-200
                  transform hover:scale-105 active:scale-95 flex items-center justify-center
                  ${plan.buttonStyle}
                `}
                            >
                                {plan.buttonText}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Platform Benefits */}
            <div className="bg-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            {t('pricing.benefits_title', 'Why Choose Our Platform')}
                        </h2>
                        <p className="text-xl text-gray-600">
                            {t('pricing.benefits_subtitle', 'Comprehensive tools for education and development')}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {[
                            { icon: Code2, title: t('pricing.benefit_sandbox_title', 'Interactive Sandboxes'), desc: t('pricing.benefit_sandbox_desc', 'Create and share interactive development environments') },
                            { icon: Bot, title: t('pricing.benefit_ai_title', 'AI Tutors'), desc: t('pricing.benefit_ai_desc', 'Personalized learning with customizable AI assistants') },
                            { icon: Database, title: t('pricing.benefit_courses_title', 'Course Constructor'), desc: t('pricing.benefit_courses_desc', 'Build comprehensive educational courses with assignments') },
                            { icon: Users, title: t('pricing.benefit_collab_title', 'Team Learning'), desc: t('pricing.benefit_collab_desc', 'Collaborative learning and development for teams') },
                        ].map((feature, idx) => (
                            <div key={idx} className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <feature.icon className="w-8 h-8 text-purple-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                                <p className="text-gray-600">{feature.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-gray-50 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            {t('pricing.faq_title', 'Frequently Asked Questions')}
                        </h2>
                        <p className="text-xl text-gray-600">
                            {t('pricing.faq_subtitle', 'Everything you need to know about our pricing')}
                        </p>
                    </div>

                    <div className="space-y-8">
                        {[
                            {
                                q: t('pricing.faq_1_q', "Can I upgrade or downgrade my plan anytime?"),
                                a: t('pricing.faq_1_a', "Yes! You can upgrade your plan at any time.")
                            },
                            {
                                q: t('pricing.faq_2_q', "What happens to my data if I cancel?"),
                                a: t('pricing.faq_2_a', "Your data remains accessible for 30 days after cancellation. You can export or reactivate anytime during this period.")
                            },
                            {
                                q: t('pricing.faq_3_q', "Do you offer educational discounts?"),
                                a: t('pricing.faq_3_a', "Yes! We offer significant discounts for educational institutions. Contact us for details.")
                            }
                        ].map((faq, idx) => (
                            <div key={idx} className="bg-white rounded-xl p-8 shadow-sm">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.q}</h3>
                                <p className="text-gray-600">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Contact Form Modal */}
            {showContactForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900">
                                {formData.plan_type === 'enterprise'
                                    ? t('pricing.contact_form.title_enterprise', 'Contact Sales')
                                    : t('pricing.contact_form.title_standard', 'Request Standard Plan')
                                }
                            </h3>
                            <button
                                onClick={() => setShowContactForm(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {submitMessage ? (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Check className="w-8 h-8 text-green-600" />
                                </div>
                                <p className="text-lg text-gray-900">{submitMessage}</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.name', 'Name')} *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.email', 'Email')} *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="email"
                                            required
                                            value={formData.email}
                                            onChange={(e) => {
                                                const newEmail = e.target.value;
                                                setFormData({...formData, email: newEmail});

                                                // Validate email as user types, but only show error if they've entered something
                                                if (newEmail && !validateEmail(newEmail)) {
                                                    setEmailError(t('pricing.contact_form.invalid_email', 'Please enter a valid email address'));
                                                } else {
                                                    setEmailError('');
                                                }
                                            }}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                                emailError ? 'border-red-500 pr-10' : 'border-gray-300'
                                            }`}
                                        />
                                        {emailError && (
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                <AlertCircle className="h-5 w-5 text-red-500" />
                                            </div>
                                        )}
                                    </div>
                                    {emailError && (
                                        <p className="mt-1 text-sm text-red-600">{emailError}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.company', 'Company')}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => setFormData({...formData, company: e.target.value})}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.phone', 'Phone')}
                                    </label>
                                    <input
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.team_size', 'Team Size')}
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.team_size}
                                        onChange={(e) => setFormData({...formData, team_size: e.target.value})}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t('pricing.contact_form.message', 'Message')}
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={formData.message}
                                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                                        placeholder={t('pricing.contact_form.message_placeholder', 'Tell us about your requirements...')}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>

                                <button
                                    onClick={handleSubmitContactForm}
                                    disabled={isSubmitting || !formData.name || !formData.email || emailError}
                                    className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50"
                                >
                                    {isSubmitting ? t('pricing.contact_form.sending', 'Sending...') : t('pricing.contact_form.send_request', 'Send Request')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PricingPage;
